import React, { useState } from "react";
import { PortfolioProperty } from "../types/ownerPortfolio.types";
import { condominiumAccentClass } from "../utils/formatPortfolioDisplay";
import { getPortfolioPropertyText } from "../utils/getPortfolioPropertyText";
import { PortfolioPanelButtons, PortfolioPanels } from "./PortfolioPanels";
import { SITUATION_BADGE_CLASS } from "./situationStyles";

const HEADERS = [
  "Contrato",
  "Aluguel",
  "Condomínio",
  "Proprietário",
  "Unidade",
  "Metragem",
  "Situação",
  "Início",
  "Fim",
  "Ações",
];

interface PortfolioPropertyTableProps {
  properties: PortfolioProperty[];
  accessToken: string | null;
}

function PortfolioTableRow({
  property,
  accessToken,
}: {
  property: PortfolioProperty;
  accessToken: string | null;
}) {
  const [insightOpen, setInsightOpen] = useState(false);
  const [inspectionOpen, setInspectionOpen] = useState(false);
  const text = getPortfolioPropertyText(property);

  return (
    <>
      <tr
        className={`border-b border-white/[0.06] border-l-2 ${condominiumAccentClass(property.condominium_name)}`}
        data-testid={`portfolio-row-${property.kenlo_property_id || property.contract_code}`}
      >
        <td className="whitespace-nowrap px-3 py-2.5 align-top text-xs font-semibold text-[#f0f6fc]">{text.contract}</td>
        <td className="whitespace-nowrap px-3 py-2.5 align-top text-xs font-semibold text-[#f0f6fc]">{text.rent}</td>
        <td className="max-w-[16rem] px-3 py-2.5 align-top text-xs text-[#f0f6fc]">
          <p className="m-0 font-medium leading-snug">{text.place.headline}</p>
          {text.place.condominium && (
            <p className="m-0 mt-0.5 leading-snug text-[#8b949e]">{text.place.condominium}</p>
          )}
          {text.place.address && (
            <p className="m-0 mt-0.5 leading-snug text-[#8b949e]">{text.place.address}</p>
          )}
        </td>
        <td className="max-w-[11rem] px-3 py-2.5 align-top text-xs leading-snug text-[#f0f6fc]">{text.owner}</td>
        <td className="whitespace-nowrap px-3 py-2.5 align-top text-xs text-[#f0f6fc]">{text.unit}</td>
        <td className="whitespace-nowrap px-3 py-2.5 align-top text-xs text-[#f0f6fc]">{text.area}</td>
        <td className="px-3 py-2.5 align-top">
          <span
            className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[0.65rem] font-semibold leading-none ${SITUATION_BADGE_CLASS[property.situation]}`}
          >
            {text.situation}
          </span>
          {text.daysVacant && <p className="m-0 mt-1 text-[0.65rem] text-[#8b949e]">{text.daysVacant}</p>}
          {property.diagnosis_label && (
            <p className="m-0 mt-1 text-[0.65rem] text-[#8b949e]">{property.diagnosis_label}</p>
          )}
        </td>
        <td className="whitespace-nowrap px-3 py-2.5 align-top text-xs text-[#f0f6fc]">{text.contractStart}</td>
        <td className="whitespace-nowrap px-3 py-2.5 align-top text-xs text-[#f0f6fc]">{text.contractEnd}</td>
        <td className="px-2 py-2.5 align-top">
          <PortfolioPanelButtons
            property={property}
            insightOpen={insightOpen}
            inspectionOpen={inspectionOpen}
            onToggleInsight={() => {
              setInsightOpen((open) => !open);
              setInspectionOpen(false);
            }}
            onToggleInspection={() => {
              setInspectionOpen((open) => !open);
              setInsightOpen(false);
            }}
          />
        </td>
      </tr>
      {(insightOpen || inspectionOpen) && (
        <PortfolioPanels
          property={property}
          accessToken={accessToken}
          insightOpen={insightOpen}
          inspectionOpen={inspectionOpen}
          onCloseInspection={() => setInspectionOpen(false)}
          onCloseInsight={() => setInsightOpen(false)}
        />
      )}
    </>
  );
}

export default function PortfolioPropertyTable({
  properties,
  accessToken,
}: PortfolioPropertyTableProps) {
  return (
    <div className="overflow-x-auto" data-testid="portfolio-table">
      <table className="w-full min-w-[960px] border-collapse text-left">
        <caption className="sr-only">Meus imóveis</caption>
        <thead>
          <tr className="border-b border-white/[0.08]">
            {HEADERS.map((header) => (
              <th
                key={header}
                scope="col"
                className="px-3 py-2 text-[0.65rem] font-semibold uppercase tracking-wide text-[#6e7681]"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {properties.map((property, index) => (
            <PortfolioTableRow
              key={`${property.kenlo_property_id}-${property.contract_code ?? index}`}
              property={property}
              accessToken={accessToken}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
